import { FC } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { CustomButton } from '@components';
import { CLIENT_USER_STATUS } from '@api/types/client-user-management';
import { useRevokeUserAccessMutation } from '@api/clientUserManagement';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import { USER_ACTION_SUCCESS_TOASTS } from '../../constants';

const dialogPaperSx = {
  borderRadius: '28px',
  maxWidth: '520px',
};

type RemoveAccessModalCopy = {
  title: string;
  description: string;
  footnote: string;
  confirmLabel: string;
};

type RemovableUserStatus = Exclude<
  CLIENT_USER_STATUS,
  CLIENT_USER_STATUS.REVOKED | CLIENT_USER_STATUS.PENDING_DELETION
>;

const REMOVE_ACCESS_MODAL_COPY: Record<RemovableUserStatus, RemoveAccessModalCopy> = {
  [CLIENT_USER_STATUS.ACTIVE]: {
    title: 'Remove User Access?',
    description: 'Removing this user will immediately revoke their access to the dashboard',
    footnote: 'This action cannot be undone.',
    confirmLabel: 'Remove Access',
  },
  [CLIENT_USER_STATUS.INVITED]: {
    title: 'Remove Invited User?',
    description: 'Removing this user will immediately revoke their access to the dashboard',
    footnote: 'This action cannot be undone.',
    confirmLabel: 'Remove Access',
  },
};

const isAccessAlreadyRemoved = (
  status: CLIENT_USER_STATUS,
): status is CLIENT_USER_STATUS.REVOKED | CLIENT_USER_STATUS.PENDING_DELETION =>
  status === CLIENT_USER_STATUS.REVOKED || status === CLIENT_USER_STATUS.PENDING_DELETION;

interface RemoveAccessModalProps {
  open: boolean;
  selectedUserEmail: string;
  selectedUserStatus: CLIENT_USER_STATUS;
  selectedUserMembershipGrantId: string;
  clientCode: string;
  onClose: () => void;
  onSubmit?: () => void;
}

const RemoveAccessModal: FC<RemoveAccessModalProps> = ({
  open,
  clientCode,
  selectedUserEmail,
  selectedUserStatus,
  selectedUserMembershipGrantId,
  onClose,
  onSubmit,
}) => {
  const dispatch = useDispatch();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [revokeUserAccess, { isLoading: isRevoking }] = useRevokeUserAccessMutation();

  if (isAccessAlreadyRemoved(selectedUserStatus)) {
    return null;
  }

  const { title, description, footnote, confirmLabel } =
    REMOVE_ACCESS_MODAL_COPY[selectedUserStatus];

  const handleClose = () => {
    if (isRevoking) return;
    onClose();
  };

  const handleRemoveAccess = async () => {
    if (isRevoking) return;
    try {
      await revokeUserAccess({
        clientCode,
        membershipGrantId: selectedUserMembershipGrantId,
      }).unwrap();
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.SUCCESS,
          message: USER_ACTION_SUCCESS_TOASTS.ACCESS_REMOVED,
        }),
      );
      onSubmit?.();
      onClose();
    } catch (error) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Error while removing access',
        }),
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      aria-labelledby="remove-access-dialog-title"
      sx={{
        '& .MuiDialog-paper': {
          ...dialogPaperSx,
          width: isMobile ? 'min(90vw, 400px)' : '520px',
        },
      }}
    >
      <DialogTitle
        id="remove-access-dialog-title"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          py: '1.5rem',
        }}
      >
        <Typography
          component="span"
          sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: '2rem', color: '#162936' }}
        >
          {title}
        </Typography>
        <IconButton aria-label="close" onClick={handleClose} disabled={isRevoking} sx={{ p: 0 }}>
          <CloseOutlinedIcon sx={{ fontSize: '1.5rem', color: '#162936' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          color: '#162936',
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
        }}
      >
        <Typography sx={{ fontWeight: 500 }}>{description}</Typography>
        <Typography sx={{ fontWeight: 700 }}>{selectedUserEmail}</Typography>
        <Typography sx={{ fontWeight: 500 }}>{footnote}</Typography>
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          padding: '1.5rem',
          gap: '0.5rem',
        }}
      >
        <CustomButton
          name="Cancel"
          onClick={handleClose}
          disabled={isRevoking}
          backgroundColor="#FFF"
          color="#51B8B0"
        />
        <CustomButton
          name={isRevoking ? 'Removing...' : confirmLabel}
          onClick={handleRemoveAccess}
          disabled={isRevoking}
          backgroundColor="#FF6347"
          color="#FFF"
        />
      </DialogActions>
    </Dialog>
  );
};

export default RemoveAccessModal;
